import React, { useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import classNames from 'classnames'

import './DropDownAddCollection.scss';
import { ReactComponent as PlusCircle } from 'static/icons/plus-circle.svg';
import { ReactComponent as Arrow } from 'static/icons/arrow.svg';
import { ReactComponent as Triangle } from 'static/icons/triangle.svg';

import useDrop from "../../../hooks/useDrop";
import { useDispatch, useSelector } from "react-redux";
import { getCollectionsForFolder } from "../../../utils/builder/IScheduleLogic";
import {
  addToNewCollection,
  copyToCollection,
  copyToCollectionUnsaved
} from "../../../store/actions/collections";
import { showToast } from "utils/helpers";
import { tabType } from "utils/data";
import { getBounding } from "utils/elementExt";

const DropDownAddCollection = ({ tableInfo, unstyled }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { collectionsFolders } = useSelector((state) => state.viewer);
  const { tabs } = useSelector((state) => state.table);

  const blockRef = useRef();
  const collections = useRef();

  const [active, setActive] = useDrop(blockRef);
  const [dropDownSize, setDropDownSize] = useState(null);
  const [currentCollectionFolder, setCurrentCollectionFolder] = useState(null);
  const [collectionPosition, setCollectionPosition] = useState(null);
  const [activeCollections, setActiveCollections] = useState(null);

  const handleSwitch = (event) => {
    const margin = 85;
    const availableHeight = window.innerHeight - event.clientY - margin;
    setDropDownSize(availableHeight);

    setActive((prevState) => !prevState);
  };

  const openCollectionsList = async (event, name) => {
    await getCollections(name);

    if (name === currentCollectionFolder) {
      closeCollectionsList();
    } else {
      setCurrentCollectionFolder(name);
    }

    const folderButtonClientRect = getBounding(event.target);
    const collectionsClientRect = getBounding(collections.current);
    const heightWindow = document.body.scrollHeight;

    const clientX = folderButtonClientRect?.left - collectionsClientRect?.width - 10;
    let clientY = folderButtonClientRect?.top - 10;

    if (heightWindow - folderButtonClientRect?.y < collectionsClientRect?.height) {
      clientY = clientY - collectionsClientRect.height + 50;
    }

    setCollectionPosition({ clientX, clientY });
  }

  const closeCollectionsList = () => {
    setCurrentCollectionFolder(false);
  }

  const isScroll = () => {
    closeCollectionsList();
  }

  const getCollections = async (folderName) => {
    const collections = await getCollectionsForFolder(folderName);
    setActiveCollections(collections);
  }

  const createNewCollection = async () => {
    try {
      await dispatch(addToNewCollection({ elements: tableInfo.lineElements.flat(2), properties: tableInfo?.collection.properties, groups: tableInfo?.collection.groupBy }));
      showToast(true, t('selection_created'));
    } catch (error) {
      showToast(false, t('selection_not_created'));
    }
    closeCollectionsList();
    setActive(null);
  }

  const handleClickCollection = async (collection) => {
    try {
      dispatch(copyToCollection({ title: collection.title, folder: currentCollectionFolder, id: collection.creationTime }));
      showToast(true, `${t('added_to')} ${collection.title}`);
    } catch (error) {
      showToast(false, t('something_went_wrong'));
    }
    closeCollectionsList();
    setActive(null);
  }


  const handleClickCollectionUnsaved = async (tab) => {
    try {
      dispatch(copyToCollectionUnsaved({ elements: tableInfo.lineElements.flat(2), collection: tab.collection, id: tab.id }));
      showToast(true, `${t('added_to')} ${tab.name}`);
    } catch (error) {
      showToast(false, t('something_went_wrong'));
    }
    closeCollectionsList();
    setActive(null);
  }

  const condition = active ? tableInfo?.rowsData?.length > 5 ? "active-drop" : "active-drop-small" : "";

  return (
    <div className={classNames( 'drop-down-add-to', { 'drop-down-add-to--unstyled': unstyled })} ref={blockRef}>
      <div className="drop-down-add-to__wrapper">
        <div className={classNames( 'drop-down-add-to__btn', { 'buttonUnstyled': unstyled })} onClick={(event) => handleSwitch(event)}>
          {unstyled ? null : <PlusCircle className="drop-down-add-to__plus-circle" />}

          {t('add_to')}

          <Arrow className={classNames('arrow-icon', { active })} />
        </div>

        <div className={`drop-down-add-to__box-wrapper ${condition}`}>
          <div className="drop-down-add-to__box-scroll">
            <div className="drop-down-add-to__box"
              style={dropDownSize < 600 ? { maxHeight: dropDownSize + "px" } : { maxHeight: "600px" }}
              onScroll={isScroll}>

              <p className="drop-down-add-to__text" onClick={createNewCollection}>{t('new_selection_set')}</p>

              <div className="drop-down-add-to__line"></div>

              {
                tabs.map((tab) => {
                  if (tab.type === tabType.unsaved) {

                    return (
                      <p key={tab.name} className="drop-down-add-to__text-unsaved" onClick={() => handleClickCollectionUnsaved(tab)}>
                        {tab.name}<span className="drop-down-add-to__unsaved"> - {t('unsaved')}</span>
                      </p>
                    )
                  }
                })
              }

              {
                tabs?.some((tab) => tab.type === tabType.unsaved) &&
                <div className="drop-down-add-to__line"></div>
              }

              {
                collectionsFolders?.map((collectionFolder) => {
                  return (
                    <p className={`drop-down-add-to__text ${currentCollectionFolder === collectionFolder && "drop-down-add-to__text-active"}`}
                      onClick={(event) => openCollectionsList(event, collectionFolder)}
                      key={collectionFolder}>
                      <Triangle className="drop-down-add-to__triangle" />
                      {collectionFolder}
                    </p>
                  )
                })
              }
            </div>
          </div>

          <div className={`drop-down-add-to__box-collections-wrapper ${currentCollectionFolder && "drop-down-add-to__box-collections-open"}`}
            ref={collections}
            style={{ top: collectionPosition?.clientY + "px", insetInlineStart: collectionPosition?.clientX + "px" }}>
            <div className="drop-down-add-to__box-collections"
              style={{ maxHeight: dropDownSize < 600 ? dropDownSize - dropDownSize / 3 + "px" : "600px" }}>
              {
                activeCollections?.length ?
                  activeCollections.map((collection) => {
                    return (
                      <p key={collection.title} className="drop-down-add-to__text" onClick={() => handleClickCollection(collection)}>{collection.title}</p>
                    )
                  }) :
                  <p className="drop-down-add-to__text">{t('folder_empty')}</p>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DropDownAddCollection;
